import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, Button, Typography } from '@mui/material';
import React from 'react';
import { theme } from '../../../styles/theme';
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat"; // for 'Do' format like 11th, 12th
import { useNavigate } from 'react-router-dom';
dayjs.extend(advancedFormat);

function ManageEventsCardComponent({
    eventInfo,
    handleSubmitApplication = () => console.log('handleSubmitApplication clicked'),
    handleDelete = () => console.log('handleDelete clicked'),
    handleWithdraw = () => console.log('handleWithdraw clicked'),

    eventApplication = false }) {

    const navigate = useNavigate();
    const [vendorDialogOpen, setVendorDialogOpen] = React.useState(false);

    const status = {
        "draft": {
            label: "DRAFT",
            color: theme.colors.red,
            borderColor: theme.colors.red
        },
        "pending": {
            label: "PENDING",
            color: theme.colors.red,
            borderColor: theme.colors.red
        },
        "review": {
            label: "IN REVIEW",
            color: theme.colors.secondary,
            borderColor: theme.colors.secondary
        },
        "published": {
            label: "PUBLISHED",
            color: 'white',
            borderColor: theme.colors.events.primary
        },
        "paid": {
            label: "ACCEPTED",
            color: 'white',
            borderColor: theme.colors.events.primary
        }
    }

    const eventDetails = {
        name: eventApplication ? eventInfo?.event_name : eventInfo?.name,
        slug: eventApplication ? eventInfo?.event_slug : eventInfo?.slug,
        status: eventApplication ? eventInfo?.application_status : eventInfo?.status,
        start_time: eventApplication ? null : eventInfo?.start_time,
    };

    return (
        <>
            <Accordion
                square={true}
                sx={{
                    borderRadius: 5,
                    overflow: "hidden",
                    "&:before": { display: "none" } // Removes the default divider line
                }}
            >
                {/* Accordion Header */}
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon className='manage-card-icon-circle' sx={{ color: theme.colors.events.primary }} />}>
                    <Box pr={1}>
                        <Box display={'flex'} alignItems={'center'} maxWidth={{ md: '650px', sm: '600px', xs: '300px' }}>
                            <Typography variant='h6' color={theme.colors.events.black} sx={{ display: "inline" }}>
                                {eventDetails?.name || 'N/A'}
                            </Typography>
                            <Chip label={status[eventDetails?.status != null ? eventDetails.status : "draft"]?.label} variant="outlined" size="small"
                                sx={{
                                    color: status[eventDetails?.status != null ? eventDetails.status : "draft"]?.color,
                                    borderColor: status[eventDetails?.status != null ? eventDetails.status : "draft"]?.borderColor,
                                    bgcolor: ["published", "paid"].includes(eventDetails?.status)
                                        ? status[eventDetails?.status != null ? eventDetails.status : "draft"]?.borderColor
                                        : 'transparent',
                                    ml: 1
                                }} />
                        </Box>
                        <Typography variant='body2' color={'textSecondary'}>
                            {eventDetails?.start_time ? dayjs(eventDetails.start_time).format('Do MMMM YYYY [at] h:mm A') : null}
                        </Typography>
                    </Box>
                </AccordionSummary>
                {/* Accordion Content */}
                <AccordionDetails sx={{ py: 0, mt: -2 }}>
                    <List>
                        {!eventApplication ? (
                            eventDetails?.status == "published" ? (
                                <ListItemButton onClick={() => navigate(`/_/events/organizer/approve/${eventDetails.slug}`)}>
                                    <ListItemText primary="✅" sx={{ maxWidth: "35px" }} />
                                    <ListItemText primary="Approve Vendors" />
                                </ListItemButton>
                            ) : (
                                <ListItemButton onClick={() => handleSubmitApplication(eventDetails.slug)}>
                                    <ListItemText primary="✅" sx={{ maxWidth: "35px" }} />
                                    <ListItemText primary="Publish Application" />
                                </ListItemButton>
                            )) : (
                            <ListItemButton onClick={() => setVendorDialogOpen(true)}>
                                <ListItemText primary="🏷️" sx={{ maxWidth: "35px" }} />
                                <ListItemText primary="View Application" />
                            </ListItemButton>
                        )
                        }
                        {
                            eventApplication && (
                                <ListItemButton onClick={() => handleWithdraw(eventDetails.slug, eventInfo?.storefront_slug)}>
                                    <ListItemText primary="💣" sx={{ maxWidth: "35px" }} />
                                    <ListItemText primary="Withdraw Application" />
                                </ListItemButton>
                            )
                        }
                        {!eventApplication &&
                            <ListItemButton onClick={() => navigate(`/_/events/organizer/update/${eventDetails.slug}`)}>
                                <ListItemText primary="✏️" sx={{ maxWidth: "35px" }} />
                                <ListItemText primary="Edit Event" />
                            </ListItemButton>
                        }
                        <ListItemButton onClick={() => navigate(`/event/${eventDetails.slug}`)}>
                            <ListItemText primary="👀" sx={{ maxWidth: "35px" }} />
                            <ListItemText primary="Preview Event" />
                        </ListItemButton>
                        {!eventApplication &&
                            <ListItemButton onClick={() => handleDelete(eventDetails.slug)}>
                                <ListItemText primary="💣" sx={{ maxWidth: "35px" }} />
                                <ListItemText primary="Delete Event" />
                            </ListItemButton>
                        }
                    </List>
                </AccordionDetails>
            </Accordion>
            <Dialog
                open={vendorDialogOpen}
                onClose={() => setVendorDialogOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: 4 } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>Application Summary</DialogTitle>
                <DialogContent dividers sx={{ pt: 2 }}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            📍 {eventInfo?.event_name || 'Event'}
                        </Typography>
                        <Typography variant="body1">
                            🏕️ <strong>Booth Type:</strong> {eventInfo?.booth_type_name || 'N/A'} (${eventInfo?.booth_type_cost || '0'})
                        </Typography>
                        <Typography variant="body1">
                            🕓 <strong>Submitted:</strong> {eventInfo?.submitted_at ? new Date(eventInfo.submitted_at).toLocaleString('en-US', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: 'numeric',
                                hour12: true
                            }) : 'N/A'}
                        </Typography>
                        <Typography variant="body1">
                            📌 <strong>Status:</strong> {eventInfo?.application_status || 'N/A'}
                        </Typography>
                    </Box>
                    <a
                        href={`/event/${eventInfo?.event_slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                    >
                        <Button
                            variant="text"
                            color="primary"
                            startIcon={<OpenInNewIcon />}
                            sx={{ mt: 2, borderRadius: 3 }}
                        >
                            View Event Page
                        </Button>
                    </a>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'flex-end', px: 3, py: 2 }}>
                    <Button sx={{ borderRadius: 4, px: 3 }} onClick={() => setVendorDialogOpen(false)} variant="outlined" color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ManageEventsCardComponent;